import React, { useEffect, useMemo } from 'react';
import ToggleButton from 'components/Buttons/ToggleButton';
import { usePerms } from 'contexts/PermsContext';
import SearchBar from 'components/SearchBar/SearchBar';
import useSearch from 'hooks/useSearch';

const PermissionsTab = ({ formData, setFormData, permList, edit = true, roleid = 0 }) => {
  const perms = usePerms();

  const search = useSearch();

  const enableLinkToggle = (array, index) => {
    const temp = [...array];

    permList
      .filter((item) => {
        return item?.enableLinked > 0;
      })
      .forEach((item) => {
        if (index > item.index && index <= item.index + item.enableLinked) temp[item.index] = true;
      });

    return temp;
  };

  const { visibleIndices, visiblePermList } = useMemo(() => {
    const indices = new Set();
    const list = (permList || []).filter((filter, index) => {
      let visible = false;
      if (filter.linked === 0) {
        visible = perms.testPermission(
          [8, 9, 10, 11, 12, 13],
          [filter.name.replace(/\s/g, '').replace(/\//g, '-').replace(/:/g, '_')],
        );
      } else {
        for (let i = 1; i <= filter.linked; i++) {
          const linkedPerm = permList[index + i];
          if (!linkedPerm) continue;
          if (
            perms.testPermission(
              [8, 9, 10, 11, 12, 13],
              [linkedPerm.name.replace(/\s/g, '').replace(/\//g, '-').replace(/:/g, '_')],
            )
          ) {
            visible = true;
            break;
          }
        }
      }
      if (visible) indices.add(index);
      return visible;
    });
    return { visibleIndices: indices, visiblePermList: list };
  }, [permList, perms]);

  useEffect(() => {
    if (!permList?.length || !formData.permissions?.length) return;

    let changed = false;
    const nextPermissions = [...formData.permissions];

    permList.forEach((item) => {
      if (item.linked <= 0) return;
      let visibleLinkedCount = 0;
      let linkEnabled = true;

      for (let i = 0; i < item.linked; i++) {
        const linkedIndex = item.index + 1 + i;
        if (linkedIndex >= nextPermissions.length) continue;
        if (!visibleIndices.has(linkedIndex)) continue;
        visibleLinkedCount += 1;
        if (!nextPermissions[linkedIndex]) linkEnabled = false;
      }

      if (visibleLinkedCount > 0 && linkEnabled !== nextPermissions[item.index]) {
        nextPermissions[item.index] = linkEnabled;
        changed = true;
      }
    });

    if (changed) {
      setFormData((prev) => ({
        ...prev,
        permissions: nextPermissions,
      }));
    }
  }, [permList, formData.permissions, setFormData, visibleIndices]);

  return (
    <div className="w-100 h-100 d-flex flex-column overflow-hidden" style={{ minHeight: 0 }}>
      <div className="d-flex flex-row align-items-center justify-content-between px-3 pt-3 pb-2 gap-2 flex-shrink-0">
        <div className="h4 fw-bolder m-0">Permissions</div>
        <div>
          <SearchBar className="" placeholder="Search..." />
        </div>
      </div>
      <div className="border-bottom border-2 border-light-subtle opacity-50 mx-3" />
      <div
        className="customScrollbar-Y overflow-y-auto overflow-x-hidden flex-grow-1 px-3 pt-2"
        style={{ minHeight: 0 }}
      >
        {visiblePermList.map((item) => {
              let linkEnabled = true;
              let visibleLinkedCount = 0;
              const visibleLinkedIndices = item.name === 'System' ? [] : null;
              for (let i = 0; i < item.linked; i++) {
                const linkedIndex = item.index + 1 + i;
                if (linkedIndex >= formData.permissions.length) continue;
                if (!visibleIndices.has(linkedIndex)) continue;
                visibleLinkedCount += 1;
                if (visibleLinkedIndices) visibleLinkedIndices.push(linkedIndex);
                if (!formData.permissions[linkedIndex]) {
                  linkEnabled = false;
                }
              }

              if (
                (item.display &&
                  item.display
                    .toLowerCase()
                    .replace(/ /g, '')
                    .includes(search.toLowerCase().replace(/ /g, ''))) ||
                item.name
                  .toLowerCase()
                  .replace(/ /g, '')
                  .includes(search.toLowerCase().replace(/ /g, '')) ||
                item.desc
                  .toLowerCase()
                  .replace(/ /g, '')
                  .includes(search.toLowerCase().replace(/ /g, ''))
              )
                return (
                  <div key={item.index}>
                    {item.linked > 0 && item.index != 0 && (
                      <div className="border-bottom border-2 border-light-subtle opacity-50" />
                    )}
                    <div className="ms-3 d-flex flex-row justify-content-between align-items-center fs-16 my-2 gap-3">
                      {/* Permission Name */}
                      <div>
                        <div className="fw-bold">{item.display ? item.display : item.name}</div>
                        <div>{item.desc}</div>
                      </div>
                      <div
                        className="me-3 d-flex align-items-center justify-content-end"
                        style={{ width: 200, height: 71 }}
                      >
                        {/* Permission Toggle Button */}
                        <ToggleButton
                          isOff={
                            parseInt(roleid) === -1 || parseInt(roleid) === -3
                              ? false
                              : item.linked > 0 && visibleLinkedCount > 0
                                ? !linkEnabled
                                : !formData.permissions[item.index]
                          }
                          onClick={() => {
                          if (!edit) return;
                          if (item.linked === 0) {
                            setFormData((prev) => {
                              let temp = [...prev.permissions];
                              temp[item.index] = !temp[item.index];

                              if (temp[item.index]) temp = [...enableLinkToggle(temp, item.index)];
                              else if (permList[item.index]?.enableLinked > 0) {
                                for (let x = 1; x <= permList[item.index].enableLinked; x++) {
                                  const linkedIndex = item.index + x;
                                  if (linkedIndex >= temp.length) continue;
                                  temp[linkedIndex] = false;
                                }
                              }

                              return { ...prev, permissions: temp };
                            });
                          } else {
                            setFormData((prev) => {
                              let temp = [...prev.permissions];
                              temp[item.index] = !temp[item.index];

                              if (temp[item.index]) temp = [...enableLinkToggle(temp, item.index)];
                              else if (permList[item.index]?.enableLinked > 0) {
                                for (let x = 1; x <= permList[item.index].enableLinked; x++) {
                                  const linkedIndex = item.index + x;
                                  if (linkedIndex >= temp.length) continue;
                                  temp[linkedIndex] = false;
                                }
                              }

                              for (let i = 0; i < item.linked; i++) {
                                const linkedIndex = item.index + 1 + i;
                                if (linkedIndex >= temp.length) continue;
                                temp[linkedIndex] = temp[item.index];
                              }

                              return { ...prev, permissions: temp };
                            });
                          }
                        }}
                      />
                      </div>
                    </div>
                    {item.linked > 0 && (
                      <div className="border-bottom border-3 border-light-subtle opacity-75" />
                    )}
                  </div>
                );
            })}
      </div>
    </div>
  );
};

export default PermissionsTab;
